/**
 * Format a date string to show relative time (e.g., "8 days ago")
 * If the date is today, returns "Today"
 * Otherwise returns "X days ago" or the formatted date
 */
export const formatRelativeDate = (dateString: string | null): string => {
  if (!dateString) {
    return 'Never';
  }

  // Parse YYYY-MM-DD as local date, not UTC
  // new Date("2025-01-15") interprets as UTC, causing timezone issues
  // Instead, manually parse and create local date
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return '1 day ago';
  } else if (diffDays > 0) {
    return `${diffDays} days ago`;
  } else {
    // Future date (shouldn't happen for attendance)
    return dateString;
  }
};

/**
 * Format date string with relative time and absolute date
 * e.g., "8 days ago (8/30/25)"
 */
export const formatRelativeWithDate = (dateString: string | null): string => {
  if (!dateString) {
    return 'Never';
  }

  const relative = formatRelativeDate(dateString);

  // Parse YYYY-MM-DD as local date to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed

  const formatted = date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit'
  });

  if (relative === 'Today') {
    return `Today (${formatted})`;
  }

  return `${relative} (${formatted})`;
};
